import { use, useCallback, useState } from 'react'

import { MarkAsSafeContainerProps } from '@chat/Dialog/MessageItem/ContextMenu/types'

import { Trans, useTranslation } from 'react-i18next'

import { useSearchParams, useNavigate } from 'react-router-dom'

import { fetchMessageById, markMessageAsSafe } from '@app/api'

import { ChatContext } from '@app/context'

import { useConversationActorRole } from '@app/hooks'
import { useNotificationsActions } from '@app/hooks/useNotificationsActions'
import { trackMarkMessageAsSafe } from '@app/services/tracking'

import { Button } from '@lib/components'

import { TextWithBullet } from './TextWithBullet'
import { handleConversationStatusChange } from './handleConversationStatusChange'
import styles from './styles.module.scss'

export const MarkAsSafeContainer = ({
  conversationId,
  message,
  onClose,
  abortController,
}: MarkAsSafeContainerProps) => {
  const { dispatch } = use(ChatContext)
  const conversationActorRole = useConversationActorRole()
  const { t } = useTranslation()
  const isBlacklisted = !!message.blacklistEntryIds?.length
  const [busy, setBusy] = useState(false)
  const { showError } = useNotificationsActions()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const handleConfirm = useCallback(async () => {
    setBusy(true)
    try {
      await markMessageAsSafe(
        conversationId,
        message.id,
        abortController()?.signal
      )
      trackMarkMessageAsSafe(conversationId, message.id, conversationActorRole)
      const msg = await fetchMessageById(
        conversationId,
        message.id,
        abortController()?.signal
      )
      dispatch({
        type: 'MESSAGE_UPDATED',
        payload: { conversationId, message: msg.message },
      })
      handleConversationStatusChange(conversationId, searchParams, navigate, dispatch)
    } catch (e) {
      showError()
      console.error('Error marking message as safe', e)
    } finally {
      onClose()
    }
  }, [])

  return isBlacklisted ? (
    <div className={styles.textContainer}>
      <div className={styles.textMain}>
        {t('Chat.Message.CannotMarkAsSafeDueToBlacklist')}
      </div>
      <div className={styles.textSecondary}>
        {t('Chat.Message.ContactCustosToRemoveFromBlacklist')}
      </div>
    </div>
  ) : (
    <>
      <div className={styles.textContainer}>
        <div className={styles.textMain}>
          <Trans i18nKey="Chat.Message.MarkAsSafeConfirm">
            <TextWithBullet> </TextWithBullet>
          </Trans>
        </div>
        <div className={styles.textSecondary}>
          {t('Chat.Message.MarkAsSafeConfirmHint')}
        </div>
      </div>
      <div className={styles.buttons}>
        <Button variant={'secondary'} onClick={onClose}>
          {t('Common.Button.Cancel')}
        </Button>
        <Button
          variant={'primary'}
          className={busy ? styles.busy : ''}
          onClick={handleConfirm}
        >
          {t('Common.Button.Confirm')}
        </Button>
      </div>
    </>
  )
}
